/**
 * Responsibility: everything about this installation, kept in one shape.
 *
 * **General** - the numbers this person assumes, their storage, their data.
 * Everyone sees it. **Keys**, **Nodes**, **Index** and **Data Hub** are
 * operator business and are shown to an administrator only.
 *
 * Index and Data Hub are deliberately apart. Both answer "how much data is
 * there", and they are two different bodies of data filled by two different
 * jobs: the index is the catalogue the search box answers from, the corpus
 * is photographs harvested to calibrate a grade. One tab showing both would
 * let a stalled crawl and a stalled harvest hide behind one number.
 *
 * **Built on the same shell as every other page.** The tabs go in the
 * card's own first line - `PageContainer`'s `tabs` slot, which the kit
 * provides and nothing was using - so they sit flush at the top instead
 * of below a body's worth of padding. Under them a `ScrollArea` takes
 * whatever height is left, so the strip stays put and only the panel
 * moves. This page used to be one long scrolling document, and the tabs
 * left the screen as soon as you read a table.
 *
 * Hiding the administrator tabs is a courtesy to people who cannot use
 * them, not a control on people who should not: every route behind them
 * re-checks the address against the verified token, so a hidden tab
 * protects nothing on its own.
 */
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { PageContainer, PageTabs, ScrollArea } from '@kit/components';
import { useQuery } from '@tanstack/react-query';
import { hubApi } from '../api/client';
import { AccountDataCard } from '../components/settings/AccountDataCard';
import { DataHubCard } from '../components/settings/DataHubCard';
import { DiscordCard } from '../components/settings/DiscordCard';
import { GradingAssumptionsCard } from '../components/settings/GradingAssumptionsCard';
import { IndexCard } from '../components/settings/IndexCard';
import { MetricProgressCard } from '../components/settings/MetricProgressCard';
import { NodesCard } from '../components/settings/NodesCard';
import { ReadersCard } from '../components/settings/ReadersCard';
import { SecretsCard } from '../components/settings/SecretsCard';
import { StorageUsageCard } from '../components/settings/StorageUsageCard';
import { usePageState } from '../hooks/usePageState';

const GENERAL = 'general';
const KEYS = 'keys';
const NODES = 'nodes';
const INDEX = 'index';
const DATA_HUB = 'datahub';
const METRICS = 'metrics';

/** Forms read badly at full width; tables need all of it. */
const FORM_WIDTH = 680;

export function SettingsPage() {
  const account = useQuery({ queryKey: ['account'], queryFn: hubApi.account });
  const isAdmin = account.data?.is_admin ?? false;
  const [tab, setTab] = usePageState('settings.tab', GENERAL, 'durable');

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // A non-admin who had one of the other tabs open - or who stopped being
  // an admin - lands on General rather than on an empty panel.
  const open = tab !== GENERAL && !isAdmin ? GENERAL : tab;

  const tabs = [
    { value: GENERAL, label: 'General' },
    ...(isAdmin
      ? [
          { value: KEYS, label: 'Keys' },
          { value: NODES, label: 'Nodes' },
          { value: INDEX, label: 'Index' },
          { value: DATA_HUB, label: 'Data Hub' },
          { value: METRICS, label: 'Grading Metrics' },
        ]
      : []),
  ];

  return (
    <PageContainer
      disableBodyScroll={onDesktop}
      tabs={<PageTabs tabs={tabs} value={open} onChange={setTab} />}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {/* Only this scrolls. The tab strip above is the card's own row. */}
        <ScrollArea>
          {open === GENERAL ? (
            <Stack spacing={3} sx={{ maxWidth: FORM_WIDTH, pb: 2 }}>
              <GradingAssumptionsCard />
              <StorageUsageCard />
              <AccountDataCard />
            </Stack>
          ) : null}

          {open === KEYS && isAdmin && account.data ? (
            <Stack spacing={3} sx={{ maxWidth: FORM_WIDTH, pb: 2 }}>
              <SecretsCard reauthSeconds={account.data.reauth_seconds} />
              <DiscordCard reauthSeconds={account.data.reauth_seconds} />
            </Stack>
          ) : null}

          {open === NODES && isAdmin ? (
            <Stack spacing={3} sx={{ pb: 2 }}>
              <NodesCard />
              {/* Same question as the node list - is the machinery behind
                  the buttons actually running - for the part of it that
                  is somebody else's server. */}
              <ReadersCard />
            </Stack>
          ) : null}

          {/* Full width: both are tables of six columns, and squeezing them
              into the form width would wrap every row. */}
          {open === INDEX && isAdmin ? (
            <Stack sx={{ pb: 2 }}>
              <IndexCard />
            </Stack>
          ) : null}

          {open === DATA_HUB && isAdmin ? (
            <Stack sx={{ pb: 2 }}>
              <DataHubCard />
            </Stack>
          ) : null}

          {/* Full width: the table is five columns and one of them is prose. */}
          {open === METRICS && isAdmin ? (
            <Stack sx={{ pb: 2 }}>
              <MetricProgressCard />
            </Stack>
          ) : null}
        </ScrollArea>
      </Stack>
    </PageContainer>
  );
}
